// Match-impact preview popover.
//
// Pops up BEFORE creating an application so the user can see what the
// promotion does to the partner's matching-funds ceiling. Removes the
// in-head "$X committed plus $Y new… do I fit?" math.
//
// Visual: small horizontal bar (current segment + projected segment +
// ceiling outline). Status chip (comfortable / tight / breach) drives
// the bar's tint and a one-line caption. Confirm is primary, Cancel
// closes. ⏎ confirms, Esc cancels.
//
// Does NOT create the application itself — calls onConfirm(estimatedMatch)
// after the user confirms; parent owns the actual mutation.

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { formatCAD, formatCADCompact } from '../lib/format';

export interface PreviewResult {
  partner: { id: string; name: string; matching_funds_ceiling: number | null };
  grant: { id: string; title: string };
  project_id: string | null;
  match_estimated_cad: number;
  current_committed_cad: number;
  would_be_committed_cad: number;
  remaining_after_cad: number | null;
  status: 'comfortable' | 'tight' | 'breach' | 'unset';
}

interface Props {
  grantProgramId: string;
  fundingAskCad: number;
  partnerProfileId?: string;
  projectId?: string;
  /** If supplied, overrides the server-side match estimate. */
  matchCommittedCad?: number;
  onConfirm: (estimatedMatch: number) => void;
  onCancel: () => void;
  isConfirming?: boolean;
}

const STATUS_META: Record<PreviewResult['status'], { chip: string; bar: string; tintBg: string; tintBorder: string; caption: string }> = {
  comfortable: {
    chip: 'bg-emerald-200 text-emerald-900',
    bar: 'bg-emerald-600',
    tintBg: 'bg-white',
    tintBorder: 'border-neutral-200',
    caption: 'Comfortable — well within the ceiling.',
  },
  tight: {
    chip: 'bg-amber-200 text-amber-900',
    bar: 'bg-amber-500',
    tintBg: 'bg-amber-50',
    tintBorder: 'border-amber-300',
    caption: 'Tight — at 70%+ of the ceiling. Review carefully.',
  },
  breach: {
    chip: 'bg-rose-200 text-rose-900',
    bar: 'bg-rose-600',
    tintBg: 'bg-rose-50',
    tintBorder: 'border-rose-300',
    caption: 'Would breach the partner ceiling. Consider lowering the match commitment.',
  },
  unset: {
    chip: 'bg-neutral-200 text-neutral-800',
    bar: 'bg-neutral-400',
    tintBg: 'bg-white',
    tintBorder: 'border-neutral-200',
    caption: 'Partner has no matching-funds ceiling configured.',
  },
};

export function MatchImpactPreview({
  grantProgramId, fundingAskCad, partnerProfileId, projectId, matchCommittedCad,
  onConfirm, onCancel, isConfirming,
}: Props) {
  const preview = useQuery({
    queryKey: ['preview', grantProgramId, fundingAskCad, partnerProfileId ?? null, projectId ?? null, matchCommittedCad ?? null],
    queryFn: () =>
      api.post<PreviewResult>('/applications/preview', {
        grant_program_id: grantProgramId,
        funding_ask_cad: fundingAskCad,
        ...(partnerProfileId ? { partner_profile_id: partnerProfileId } : {}),
        ...(projectId ? { project_id: projectId } : {}),
        ...(matchCommittedCad !== undefined ? { match_committed_cad: matchCommittedCad } : {}),
      }),
    // Force a fresh read on each open — current_committed is live state.
    staleTime: 0,
    refetchOnMount: 'always',
  });

  // Esc cancels; Enter confirms (only when result is loaded).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
      if (e.key === 'Enter' && preview.data?.data && !isConfirming) {
        e.preventDefault();
        onConfirm(preview.data.data.match_estimated_cad);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [preview.data?.data, onConfirm, onCancel, isConfirming]);

  // The popover is a focused, in-place card the parent positions. We don't
  // own positioning — parent wraps us in an anchored container.

  if (preview.isLoading) {
    return (
      <div role="dialog" aria-modal="true" aria-label="Match-impact preview" className="rounded-md border border-neutral-200 bg-white shadow-lg p-3 w-[400px]">
        <p className="text-xs text-neutral-600">Checking partner ceiling — a moment.</p>
      </div>
    );
  }

  if (preview.error) {
    return (
      <div role="dialog" aria-modal="true" aria-label="Match-impact preview error" className="rounded-md border border-rose-200 bg-rose-50 shadow-lg p-3 w-[400px] text-xs text-rose-800">
        <p className="font-medium">Couldn't check the partner's matching-funds picture.</p>
        <p className="mt-1">
          {preview.error instanceof ApiError ? preview.error.message : 'Network error.'} You can still promote — but you won't see the ceiling impact.
        </p>
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => preview.refetch()}
            className="rounded-md border border-rose-300 px-2 py-1 hover:bg-rose-100"
          >Retry</button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-neutral-300 px-2 py-1 hover:bg-neutral-50"
          >Cancel</button>
        </div>
      </div>
    );
  }

  const r = preview.data!.data;
  const meta = STATUS_META[r.status];
  const ceiling = r.partner.matching_funds_ceiling;
  const widthCurrent = ceiling ? Math.min(100, (r.current_committed_cad / ceiling) * 100) : 0;
  const widthProjected = ceiling ? Math.min(100, ((r.would_be_committed_cad - r.current_committed_cad) / ceiling) * 100) : 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Match-impact preview — ${r.partner.name}`}
      className={'rounded-md border shadow-lg p-3 w-[400px] ' + meta.tintBorder + ' ' + meta.tintBg}
    >
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <h3 className="text-sm font-medium text-neutral-900">Match-impact preview</h3>
        <span className={'text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ' + meta.chip}>{r.status}</span>
      </div>

      <p className="text-xs text-neutral-800 leading-relaxed">
        Adding this <span className="font-medium text-neutral-900">{formatCADCompact(fundingAskCad)} {r.grant.title}</span> grant ({formatCADCompact(r.match_estimated_cad)} match commitment) would bring <span className="font-medium text-neutral-900">{r.partner.name}</span> from{' '}
        <span className="font-mono tabular-nums">{formatCADCompact(r.current_committed_cad)}</span> to{' '}
        <span className="font-mono tabular-nums">{formatCADCompact(r.would_be_committed_cad)}</span>
        {ceiling != null && (
          <> committed against{' '}
          <span className="font-mono tabular-nums">{formatCADCompact(ceiling)}</span> ceiling.{' '}
          <span className="font-medium">{formatCADCompact(r.remaining_after_cad ?? 0)} remaining.</span></>
        )}
        {ceiling == null && <span className="text-neutral-500"> (No ceiling configured.)</span>}
      </p>

      {ceiling != null && ceiling > 0 && (
        <div className="mt-3" role="img" aria-label={`Ceiling utilisation: ${Math.round((r.would_be_committed_cad / ceiling) * 100)}% after promotion`}>
          <div className="h-2.5 bg-neutral-200 rounded overflow-hidden flex">
            <div className="h-full bg-neutral-500" style={{ width: `${widthCurrent}%` }} />
            <div className={'h-full ' + meta.bar} style={{ width: `${widthProjected}%` }} />
          </div>
          <div className="mt-1 flex items-center justify-between text-[10px] text-neutral-600">
            <span>Current</span>
            <span>+ This grant</span>
            <span>Ceiling</span>
          </div>
        </div>
      )}

      <p className="mt-2 text-[11px] text-neutral-700">{meta.caption}</p>

      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => onConfirm(r.match_estimated_cad)}
          disabled={isConfirming}
          className="inline-flex items-center gap-2 bg-accent text-white rounded-md px-3 py-1.5 text-sm hover:bg-accent-hover disabled:opacity-50"
        >
          {isConfirming ? 'Creating…' : 'Confirm — promote to application'}
          <kbd className="font-mono text-[10px] bg-black/20 px-1 py-0.5 rounded">⏎</kbd>
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm hover:bg-neutral-50"
        >
          Cancel <kbd className="font-mono text-[10px] bg-neutral-100 px-1 rounded ml-1">Esc</kbd>
        </button>
      </div>
      <p className="mt-2 text-[10px] text-neutral-500">
        Hint: formatCAD precise — {formatCAD(r.would_be_committed_cad)} would-be committed.
      </p>
    </div>
  );
}
